// v2.2 findings #67 / #71 — "Sembrar en varias unidades".
//
// One batch arrives and is stocked into several production units. The form
// takes the batch name, stocking day, optional arrival count / average weight,
// the units and the fish per unit, and calls `stockLotIntoUnits`: the server
// creates the arrival lot (facility level) and one same-name lot per unit,
// sharing lineage, and each unit's stocking day gets its count / weight.

import React, { useMemo, useState } from 'react'

import { otherActiveLotsInUnit } from '../../utils/unitOccupancy'
import { useT } from '../../i18n'
import type { Lot } from '../../types/lot'
import { ApiError } from '../../utils/api'
import { splitFishEvenly, stockLotIntoUnits, type StockIntoUnitsResult } from '../../utils/stockLotIntoUnits'
import { farmToday } from '../../utils/farmTime'
import CapacityTablePicker, { capacityAssignmentForChoice } from '../capacity/CapacityTablePicker'
import { EggArrivalFields, emptyArrivalDraft, ownArrival, parseArrivalDraft, type ArrivalDraft } from './eggArrivalForm'

export type StockIntoUnitsFormUnit = { id: string; name: string }

type Props = Readonly<{
  farmId: string
  farmName?: string
  units: StockIntoUnitsFormUnit[]
  /** Lots of this facility, to warn before the server rejects a duplicate name. */
  existingLots: Lot[]
  defaultSpecies?: string
  /** The facility, for its Capacidad de carga tables (findings #151). */
  farmMetadata?: Record<string, unknown> | null
  disabled?: boolean
  onDone: (result: StockIntoUnitsResult) => void | Promise<void>
}>

const labelStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', fontWeight: 600, gap: 4, fontSize: 14 }
const inputStyle: React.CSSProperties = { padding: 10, borderRadius: 8, border: '1px solid #cbd5f5', fontSize: 14 }
const hintStyle: React.CSSProperties = { fontSize: 12, color: '#6b7280', fontWeight: 400 }

// Findings #85: the farm's today (UTC is already tomorrow on a Peru evening).
const todayInputValue = (): string => farmToday()

const parsePositiveInt = (raw: string): number | undefined => {
  const value = Number(raw.replaceAll(',', '').trim())
  return Number.isInteger(value) && value > 0 ? value : undefined
}

const parsePositiveNumber = (raw: string): number | undefined => {
  const value = Number(raw.replace(',', '.').trim())
  return Number.isFinite(value) && value > 0 ? value : undefined
}

const normalizeName = (value: string): string => value.trim().toLowerCase()

export function StockIntoUnitsForm({ farmId, farmName, units, existingLots, defaultSpecies, farmMetadata, disabled, onDone }: Props): React.ReactElement {
  const t = useT()
  const [lotKey, setLotKey] = useState('')
  const [stockingDay, setStockingDay] = useState(todayInputValue)
  const [species, setSpecies] = useState(defaultSpecies ?? '')
  const [totalFish, setTotalFish] = useState('')
  const [averageWeight, setAverageWeight] = useState('')
  const [notes, setNotes] = useState('')
  const [seedSupplier, setSeedSupplier] = useState('')
  const [capacityTableId, setCapacityTableId] = useState('')
  const [strain, setStrain] = useState('')
  const [selectedUnitIds, setSelectedUnitIds] = useState<string[]>([])
  // findings #109: ¿Peces u ovas? — an egg batch shares one arrival record; counts are eggs per incubator.
  const [kind, setKind] = useState<'fish' | 'eggs'>('fish')
  const [arrivalDraft, setArrivalDraft] = useState<ArrivalDraft>(() => emptyArrivalDraft(farmToday()))
  // findings #106: optional arrival weight per unit.
  const [weightByUnit, setWeightByUnit] = useState<Record<string, string>>({})
  const isEggs = kind === 'eggs'
  const [fishByUnit, setFishByUnit] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const selectedUnits = units.filter((unit) => selectedUnitIds.includes(unit.id))
  const total = parsePositiveInt(totalFish)
  const counts = selectedUnits.map((unit) => parsePositiveInt(fishByUnit[unit.id] ?? ''))
  const filledCounts = counts.filter((count): count is number => count !== undefined)
  const assigned = filledCounts.reduce((sum, count) => sum + count, 0)
  const someCounts = filledCounts.length > 0
  const allCounts = selectedUnits.length > 0 && filledCounts.length === selectedUnits.length

  // Units that already hold a lot with this name — the server would reject.
  const clashingUnitIds = useMemo(() => {
    const name = normalizeName(lotKey)
    if (!name) return new Set<string>()
    return new Set(
      existingLots
        .filter((lot) => lot.productionUnitId && normalizeName(lot.lotKey ?? '') === name)
        .map((lot) => lot.productionUnitId as string)
    )
  }, [existingLots, lotKey])

  // findings #104: units that already hold another active lot (warning only).
  const occupiedByUnit = useMemo(() => new Map(
    units.map((unit) => [unit.id, otherActiveLotsInUnit(existingLots, unit.id).map((lot) => lot.lotKey).join(', ')] as const)
  ), [existingLots, units])

  const toggleUnit = (unitId: string) => {
    setSelectedUnitIds((prev) => (prev.includes(unitId) ? prev.filter((id) => id !== unitId) : [...prev, unitId]))
  }

  const spreadEvenly = () => {
    if (!total || selectedUnits.length === 0) return
    const shares = splitFishEvenly(total, selectedUnits.length)
    setFishByUnit((prev) => {
      const next = { ...prev }
      selectedUnits.forEach((unit, index) => {
        next[unit.id] = String(shares[index])
      })
      return next
    })
  }

  const validate = (): string | null => {
    if (!lotKey.trim()) return t('lots.stockUnits.error.name')
    if (!stockingDay) return t('lots.stockUnits.error.date')
    if (selectedUnits.length < 2) return t('lots.stockUnits.error.units')
    if (selectedUnits.some((unit) => clashingUnitIds.has(unit.id))) return t('lots.stockUnits.error.nameTaken')
    if (someCounts && !allCounts) return t('lots.stockUnits.error.countsAllOrNone')
    if (total && !allCounts) return t('lots.stockUnits.error.countsNeeded')
    if (total && allCounts && assigned !== total) {
      return t('lots.stockUnits.error.countsMismatch', { assigned, total })
    }
    if (averageWeight.trim() && !parsePositiveNumber(averageWeight)) return t('lots.stockUnits.error.weight')
    if (selectedUnits.some((unit) => (weightByUnit[unit.id] ?? '').trim() && !parsePositiveNumber(weightByUnit[unit.id]))) {
      return t('lots.stockUnits.error.weight')
    }
    if (isEggs && !allCounts) return t('lots.stockUnits.error.eggCountsNeeded')
    return null
  }

  const submit = async () => {
    const problem = validate()
    if (problem) {
      setError(problem)
      return
    }
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      setError(t('lots.stockUnits.error.offline'))
      return
    }
    let eggArrival: { arrival: ReturnType<typeof ownArrival>; supplier?: string; strain?: string } | undefined
    if (isEggs) {
      const parsed = parseArrivalDraft({ ...arrivalDraft, arrivalDate: stockingDay, eggsReceived: '', eggsPerLitre: '', eggVolumeLitres: '' })
      if (!parsed.ok) {
        setError(t(parsed.errorKey))
        return
      }
      eggArrival = { arrival: ownArrival(parsed.value), supplier: seedSupplier, strain }
    }
    setError(null)
    setSaving(true)
    try {
      const result = await stockLotIntoUnits({
        farmId,
        farmName,
        lotKey,
        species,
        stockingDate: `${stockingDay}T00:00:00.000Z`,
        averageWeightG: isEggs ? undefined : parsePositiveNumber(averageWeight),
        eggArrival,
        notes,
        seedSupplier,
        strain,
        capacity: capacityAssignmentForChoice({ metadata: farmMetadata }, capacityTableId) ?? undefined,
        units: selectedUnits.map((unit, index) => ({
          productionUnitId: unit.id,
          productionUnitName: unit.name,
          ...(allCounts ? { fishCount: counts[index] } : {}),
          ...(!isEggs && parsePositiveNumber(weightByUnit[unit.id] ?? '') ? { averageWeightG: parsePositiveNumber(weightByUnit[unit.id] ?? '') } : {})
        }))
      })
      setLotKey('')
      setTotalFish('')
      setAverageWeight('')
      setNotes('')
      setSeedSupplier('')
      setStrain('')
      setCapacityTableId('')
      setSelectedUnitIds([])
      setFishByUnit({})
      setWeightByUnit({})
      setArrivalDraft(emptyArrivalDraft(farmToday()))
      await onDone(result)
    } catch (err) {
      setError(err instanceof ApiError || err instanceof Error ? err.message : t('lots.stockUnits.error.generic'))
    } finally {
      setSaving(false)
    }
  }

  const busy = Boolean(disabled) || saving

  return (
    <div data-testid="stock-into-units-form" style={{ display: 'grid', gap: 12 }}>
      <p style={{ margin: 0, fontSize: 13, color: '#4b5563' }}>{t('lots.stockUnits.intro')}</p>

      <div role="radiogroup" aria-label={t('lots.stockUnits.kind')} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {(['fish', 'eggs'] as const).map((option) => (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={kind === option}
            onClick={() => setKind(option)}
            disabled={busy}
            data-testid={'stock-into-units-kind-' + option}
            style={{ padding: '6px 12px', borderRadius: 999, border: '1px solid #cbd5f5', fontWeight: 600, background: kind === option ? '#1e3a8a' : '#ffffff', color: kind === option ? '#ffffff' : '#1e293b' }}
          >
            {t(option === 'fish' ? 'lots.stockUnits.kindFish' : 'lots.stockUnits.kindEggs')}
          </button>
        ))}
      </div>

      <label style={labelStyle}>
        {t('lots.stockUnits.name')}
        <input
          type="text"
          value={lotKey}
          onChange={(e) => setLotKey(e.target.value)}
          disabled={busy}
          placeholder={t('lots.stockUnits.namePlaceholder')}
          style={inputStyle}
          data-testid="stock-into-units-name"
        />
        <span style={hintStyle}>{t('lots.stockUnits.nameHint')}</span>
      </label>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
        <label style={labelStyle}>
          {t('lots.stockUnits.date')}
          <input type="date" value={stockingDay} onChange={(e) => setStockingDay(e.target.value)} disabled={busy} style={inputStyle} data-testid="stock-into-units-date" />
        </label>
        <label style={labelStyle}>
          {t('lots.stockUnits.species')}
          <input type="text" value={species} onChange={(e) => setSpecies(e.target.value)} disabled={busy} placeholder={t('lots.form.optionalPlaceholder')} style={inputStyle} />
        </label>
        <label style={labelStyle}>
          {t(isEggs ? 'lots.stockUnits.totalEggs' : 'lots.stockUnits.totalFish')}
          <input type="text" inputMode="numeric" value={totalFish} onChange={(e) => setTotalFish(e.target.value)} disabled={busy} placeholder={t('lots.form.optionalPlaceholder')} style={inputStyle} data-testid="stock-into-units-total" />
        </label>
        {isEggs ? null : (
          <label style={labelStyle}>
            {t('lots.stockUnits.averageWeight')}
            <input type="text" inputMode="decimal" value={averageWeight} onChange={(e) => setAverageWeight(e.target.value)} disabled={busy} placeholder={t('lots.form.optionalPlaceholder')} style={inputStyle} data-testid="stock-into-units-weight" />
            <span style={hintStyle}>{t('lots.stockUnits.averageWeightHint')}</span>
          </label>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
        <label style={labelStyle}>
          {t('lots.stockUnits.seedSupplier')}
          <input type="text" value={seedSupplier} onChange={(e) => setSeedSupplier(e.target.value)} disabled={busy} placeholder={t('lots.form.optionalPlaceholder')} style={inputStyle} data-testid="stock-into-units-supplier" />
        </label>
        <label style={labelStyle}>
          {t('lots.stockUnits.strain')}
          <input type="text" value={strain} onChange={(e) => setStrain(e.target.value)} disabled={busy} placeholder={t('lots.form.optionalPlaceholder')} style={inputStyle} data-testid="stock-into-units-strain" />
        </label>
      </div>

      <CapacityTablePicker farm={{ metadata: farmMetadata }} value={capacityTableId} onChange={setCapacityTableId} disabled={busy} testId="stock-into-units-capacity" />

      {isEggs ? (
        <fieldset style={{ border: '1px solid #bae6fd', borderRadius: 10, padding: 12, margin: 0, display: 'grid', gap: 8, background: '#f0f9ff' }} data-testid="stock-into-units-egg-arrival">
          <legend style={{ fontWeight: 600, fontSize: 14, padding: '0 4px' }}>{t('lots.stockUnits.eggArrival')}</legend>
          {/* Findings #128: the arrival data can be completed later. */}
          <div data-testid="stock-units-egg-arrival-later" style={{ fontSize: 12, color: '#64748b', marginBottom: 6 }}>{t('lots.stockUnits.eggArrivalLater')}</div>
          <EggArrivalFields draft={arrivalDraft} onChange={(update) => setArrivalDraft(update)} showArrivalDate={false} showCount={false} />
        </fieldset>
      ) : null}

      <fieldset style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 12, margin: 0 }}>
        <legend style={{ fontWeight: 600, fontSize: 14, padding: '0 4px' }}>{t(isEggs ? 'lots.stockUnits.incubators' : 'lots.stockUnits.units')}</legend>
        {units.length === 0 ? (
          <p style={{ ...hintStyle, margin: 0 }}>{t('lots.stockUnits.noUnits')}</p>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {units.map((unit) => {
              const checked = selectedUnitIds.includes(unit.id)
              const clash = clashingUnitIds.has(unit.id)
              return (
                <div key={unit.id} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 160, cursor: busy ? 'default' : 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleUnit(unit.id)}
                      disabled={busy}
                      data-testid={`stock-into-units-unit-${unit.id}`}
                    />
                    <span>{unit.name}</span>
                  </label>
                  {checked ? (
                    <input
                      type="text"
                      inputMode="numeric"
                      value={fishByUnit[unit.id] ?? ''}
                      onChange={(e) => setFishByUnit((prev) => ({ ...prev, [unit.id]: e.target.value }))}
                      disabled={busy}
                      placeholder={t(isEggs ? 'lots.stockUnits.eggsPerUnit' : 'lots.stockUnits.fishPerUnit')}
                      aria-label={t('lots.stockUnits.fishPerUnitFor', { unit: unit.name })}
                      style={{ ...inputStyle, width: 140, padding: 6 }}
                      data-testid={`stock-into-units-count-${unit.id}`}
                    />
                  ) : null}
                  {checked && !isEggs ? (
                    <input
                      type="text"
                      inputMode="decimal"
                      value={weightByUnit[unit.id] ?? ''}
                      onChange={(e) => setWeightByUnit((prev) => ({ ...prev, [unit.id]: e.target.value }))}
                      disabled={busy}
                      placeholder={t('lots.stockUnits.weightPerUnit')}
                      aria-label={t('lots.stockUnits.weightPerUnitFor', { unit: unit.name })}
                      style={{ ...inputStyle, width: 150, padding: 6 }}
                      data-testid={'stock-into-units-weight-' + unit.id}
                    />
                  ) : null}
                  {clash ? <span style={{ fontSize: 12, color: '#b91c1c' }}>{t('lots.stockUnits.nameTakenHere')}</span> : null}
                  {!clash && occupiedByUnit.get(unit.id) ? (
                    <span style={{ fontSize: 12, color: '#b45309' }} data-testid={`stock-into-units-occupied-${unit.id}`}>
                      {t('lots.unitOccupied.short', { lots: occupiedByUnit.get(unit.id) ?? '' })}
                    </span>
                  ) : null}
                </div>
              )
            })}
          </div>
        )}
        {selectedUnits.length > 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10, flexWrap: 'wrap', fontSize: 13 }}>
            <button type="button" onClick={spreadEvenly} disabled={busy || !total} data-testid="stock-into-units-spread">
              {t('lots.stockUnits.spreadEvenly')}
            </button>
            <span style={{ color: total && assigned !== total ? '#b45309' : '#4b5563' }} data-testid="stock-into-units-assigned">
              {total
                ? t('lots.stockUnits.assignedOfTotal', { assigned, total })
                : t('lots.stockUnits.assigned', { assigned, units: selectedUnits.length })}
            </span>
          </div>
        ) : null}
      </fieldset>

      <label style={labelStyle}>
        {t('lots.stockUnits.notes')}
        <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} disabled={busy} placeholder={t('lots.form.optionalPlaceholder')} style={inputStyle} />
      </label>

      {error ? <div style={{ color: '#b91c1c', fontSize: 13 }} data-testid="stock-into-units-error">{error}</div> : null}

      <div>
        <button type="button" onClick={() => void submit()} disabled={busy} data-testid="stock-into-units-submit">
          {saving ? t('lots.saving') : t('lots.stockUnits.submit')}
        </button>
      </div>
    </div>
  )
}
